import { put, call, takeLatest, all, race, take } from "redux-saga/effects";
// redux-saga 0.16 has no delay *effect*; delay is a promise helper on the root
// module and is used through call().
import { delay } from "redux-saga";
import * as Api from "./activityFormApi";
import { WS_MESSAGE } from "@common/actions/websocketActions";
import {
  fetchActivityFieldsRequest,
  fetchActivityFieldsSuccess,
  fetchActivityFieldsError,
  fetchActivityByIdRequest,
  fetchActivityByIdSuccess,
  fetchActivityByIdError,
  createActivityRequest,
  createActivitySuccess,
  createActivityAccepted,
  createActivityReady,
  createActivityError,
  updateActivityRequest,
  updateActivitySuccess,
  updateActivityAccepted,
  updateActivityReady,
  updateActivityError,
} from "./activityFormSlice";
import { toFormErrorPayload } from "../../utils/toFormErrorPayload";

function* fetchActivityFieldsSaga(action) {
  try {
    const { shortName, renderType } = action.payload;
    const response = yield call(Api.fetchActivityFields, { shortName, renderType });
    const rows =
      response?.fields ?? response?.data ?? response?.Data ?? response ?? [];
    yield put(fetchActivityFieldsSuccess(Array.isArray(rows) ? rows : []));
  } catch (error) {
    yield put(
      fetchActivityFieldsError(
        error?.message || "Failed to fetch activity fields",
      ),
    );
  }
}

function* fetchActivityByIdSaga(action) {
  try {
    const { activityId } = action.payload;
    const response = yield call(Api.fetchActivityById, { activityId });
    yield put(fetchActivityByIdSuccess(response?.Data ?? response));
  } catch (error) {
    yield put(
      fetchActivityByIdError(
        error?.message || "Failed to fetch activity",
      ),
    );
  }
}

// How long to trust the socket before falling back to polling the read API.
const PROJECTION_SOCKET_TIMEOUT_MS = 8000;
// Poll settings for the fallback: covers a dead socket, a failed projection, or a reconnect that landed mid-flight.
const PROJECTION_POLL_INTERVAL_MS = 2000;
const PROJECTION_POLL_ATTEMPTS = 15; // ~30s

// Ids cross the wire as numbers and appear in the URL as strings.
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// The activity POST returns the created record, so the created/updated frame is matched on the activity id instead.
const extractActivityId = (data) =>
  data?.Id ?? data?.activity_id ?? data?.activityId ?? null;

/**
 * Resolves once a socket frame reports this activity as projected. Other tabs of
 * the same browser receive the same frame; only a tab waiting on this id acts.
 */
export function* takeMatchingActivityEvent(eventName, activityId) {
  while (true) {
    const { payload } = yield take(WS_MESSAGE);
    if (payload?.event !== eventName) continue;
    if (!sameId(payload?.entity?.activityId, activityId)) continue;
    return true;
  }
}

/**
 * Waits for the write to become readable. Races the socket against a timeout,
 * then falls back to polling the read API. Returns false once both give up.
 */
export function* awaitActivityProjection(eventName, activityId) {
  const { seen } = yield race({
    seen: call(takeMatchingActivityEvent, eventName, activityId),
    timeout: call(delay, PROJECTION_SOCKET_TIMEOUT_MS),
  });
  if (seen) return true;

  for (let attempt = 0; attempt < PROJECTION_POLL_ATTEMPTS; attempt += 1) {
    const projected = yield call(Api.fetchActivityById, { activityId });
    if (projected && !projected.error && projected.status !== 404) return true;
    yield call(delay, PROJECTION_POLL_INTERVAL_MS);
  }
  return false;
}

export function* createActivitySaga(action) {
  try {
    const response = yield call(Api.createActivity, action.payload);
    // httpCall swallows network/5xx failures and resolves undefined, which would
    // otherwise leave the save button locked forever.
    if (!response) {
      yield put(createActivityError("Failed to create activity"));
      return;
    }
    if (response.status === 400 || response.error) {
      yield put(createActivityError(toFormErrorPayload(response, response.error || "Failed to create activity")));
      return;
    }

    const data = response?.Data ?? response;
    const activityId = extractActivityId(data);

    // Without an id there is nothing to correlate or poll, so keep the old
    // behaviour and let the form act on the acknowledgement.
    if (!activityId) {
      yield put(createActivitySuccess(data));
      return;
    }

    yield put(createActivityAccepted({ activityId }));
    const ready = yield call(
      awaitActivityProjection,
      "activity.created",
      activityId,
    );
    if (ready) {
      yield put(createActivityReady({ activityId }));
    } else {
      yield put(
        createActivityError(
          "The activity was created but is taking longer than expected to appear.",
        ),
      );
    }
  } catch (error) {
    yield put(createActivityError(toFormErrorPayload(error, "Failed to create activity")));
  }
}

export function* updateActivitySaga(action) {
  try {
    const response = yield call(Api.updateActivity, action.payload);
    if (!response) {
      yield put(updateActivityError("Failed to update activity"));
      return;
    }
    if (response.status === 400 || response.error) {
      yield put(updateActivityError(toFormErrorPayload(response, response.error || "Failed to update activity")));
      return;
    }

    const data = response?.Data ?? response;
    const activityId =
      extractActivityId(data) ??
      action.payload?.activity?.activity_id ??
      action.payload?.activity_id ??
      action.payload?.activityId ??
      null;

    if (!activityId) {
      yield put(updateActivitySuccess(data));
      return;
    }

    yield put(updateActivityAccepted({ activityId }));
    const ready = yield call(
      awaitActivityProjection,
      "activity.updated",
      activityId,
    );
    if (ready) {
      yield put(updateActivityReady({ activityId }));
    } else {
      yield put(
        updateActivityError(
          "The activity was updated but is taking longer than expected to appear.",
        ),
      );
    }
  } catch (error) {
    yield put(updateActivityError(toFormErrorPayload(error, "Failed to update activity")));
  }
}

export default function* activityFormSaga() {
  yield all([
    takeLatest(fetchActivityFieldsRequest.type, fetchActivityFieldsSaga),
    takeLatest(fetchActivityByIdRequest.type, fetchActivityByIdSaga),
    takeLatest(createActivityRequest.type, createActivitySaga),
    takeLatest(updateActivityRequest.type, updateActivitySaga),
  ]);
}